import { PanelProps, Panel as PrimePanel } from 'primereact/panel'
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Button } from 'primereact/button'
import { Divider } from 'primereact/divider'
import { PrimeIcons } from 'primereact/api'

import useText from '../../hooks/useText'
import { Input } from '../Input'

type Props = {
    headerActions?: ReactNode
    footerActions?: ReactNode
    className?: ClassName
    headerIcon: string | React.ReactNode
    searchValue?: string
    onSearchValueChange?: React.Dispatch<React.SetStateAction<string>>
    testId?: string
} & PanelProps

export const Panel: FC<Props> = ({
    headerActions,
    footerActions,
    headerIcon,
    className,
    onSearchValueChange,
    searchValue,
    testId = 'panel',
    ...props
}) => {
    const text = useText()

    const [isFilterVisible, setIsFilterVisible] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (isFilterVisible && inputRef.current) {
            inputRef.current.focus()
        }

        onSearchValueChange?.('')
    }, [isFilterVisible, onSearchValueChange])

    const headerTemplate = (options: { className: ClassName }) => {
        return (
            <div className={`${options.className} flex-wrap gap-3`}>
                <div className="flex gap-2 align-items-center">
                    {typeof headerIcon === 'string' ? <i className={headerIcon} /> : headerIcon}

                    <span className="p-panel-title">{props.title}</span>
                </div>

                <div className="flex align-items-center gap-3">
                    {headerActions}

                    {onSearchValueChange && headerActions && (
                        <Divider layout="vertical" className="align-self-stretch m-0 p-0" />
                    )}

                    {isFilterVisible && (
                        <Input
                            ref={inputRef}
                            id={`${testId}-filter-input`}
                            aria-label={text('lblFilterModal')}
                            type="search"
                            value={searchValue}
                            onChange={(e) => onSearchValueChange?.(e.target.value)}
                            placeholder={text('lblFilterModal')}
                            icon={PrimeIcons.SEARCH}
                            className="p-inputtext-sm"
                            inputClassName="pl-5"
                            data-testid={`${testId}-filter-input`}
                        />
                    )}

                    {onSearchValueChange && (
                        <Button
                            icon={`pi ${isFilterVisible ? 'pi-filter-slash' : 'pi-filter'}`}
                            data-testid={`${testId}-filter-toggle-btn`}
                            aria-label={text(isFilterVisible ? 'btnClearFilter' : 'lblFilterModal')}
                            outlined
                            size="small"
                            onClick={() => setIsFilterVisible(!isFilterVisible)}
                        />
                    )}
                </div>
            </div>
        )
    }

    const footerTemplate = (options: { className: ClassName }) => {
        return <div className={`${options.className} flex justify-content-end`}>{footerActions}</div>
    }

    return (
        <PrimePanel
            className={cn(className)}
            headerTemplate={headerTemplate}
            footerTemplate={footerActions && footerTemplate}
            data-testid={testId}
            {...props}
        ></PrimePanel>
    )
}
